import React, { useEffect, useRef, useState } from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Tab, Box } from '@mui/material';
import { Typography } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from '../authconfig';
import APIUrls from '../components/_api/APIUrls';
import UserAPI from '../components/_api/UserAPI';
import { InlineTextButton } from '../components/faqPage/InlineTextButton';
import { faArrowDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { grey } from '@mui/material/colors';
import './pageStyles/AccountPage.css';

interface FAQCategoryProps {
  title: string;
  //answer needs to be a react component. either typography for text or other stuff if embedding a video
  questions: { question: string; answer: React.ReactNode }[];
}

const msalInstance = new PublicClientApplication(msalConfig);
const account = msalInstance.getActiveAccount();
const websiteUrl = APIUrls.getFrontendUrl();

export function FAQ() {
  const userAPI = useRef(new UserAPI()).current;
  const [tabValue, setTabValue] = React.useState('0');
  const [userHasPremium, setUserHasPremium] = useState<boolean | null>(null);

  const handlePremiumLogic = (accountId: string) => {
    if (userHasPremium === null) {
      console.error('Please wait for premium status to be determined');
      return;
    }
    if (userHasPremium) {
      userAPI.createCustomerPortal(accountId);
    } else {
      userAPI.createCheckoutSession(accountId);
    }
  };

  useEffect(() => {
    if (msalInstance) {
      //check premium status
      userAPI.getPremiumStatus(account?.localAccountId ?? '').then((result) => {
        setUserHasPremium(result);
      });
    }
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const faqData: FAQCategoryProps[] = [
    {
      title: 'General Questions',
      questions: [
        {
          question: 'What is Dyslexico?',
          answer: (
            <Typography variant="body1" maxWidth={'900px'}>
              Dyslexico is a spellcheck and autocorrect solution optimized for people with dyslexia that provides higher
              success for dyslexic grammatical patterns, as well as an experience uniquely designed to help teach
              dyslexics to improve their writing skills as they correct.
            </Typography>
          ),
        },
        {
          question: 'Do I have to be diagnosed with dyslexia to use Dyslexico?',
          answer: (
            <Typography variant="body1" maxWidth={'900px'}>
              While our product is built with people with dyslexia in mind, you don’t need to be diagnosed! If you
              believe that existing tools aren’t catching enough of your errors, we encourage you to try using Dyslexico
              and see if there’s a difference. Did you know up to 1 in 5 people have dyslexia? That might just be you!
            </Typography>
          ),
        },
        {
          question: 'How old should I be to use Dyslexico?',
          answer: (
            <Typography variant="body1" maxWidth={'900px'}>
              Dyslexico is intended to be used by those who frequently type online, whether it’s for school assignments
              or for everyday life. For students, Dyslexico is particularly intended to help students at an age after
              their classwork has shifted more to content than proper spelling.
            </Typography>
          ),
        },
        {
          question: 'How do I purchase Dyslexico?',
          answer: msalInstance.getActiveAccount() ? (
            <>
              <Typography>
                You can use our text editor free for a limited number of times each day. For unlimited access, you can
                click{' '}
                <InlineTextButton onClick={() => handlePremiumLogic(account?.localAccountId ?? '')}>
                  here
                </InlineTextButton>{' '}
                to subscribe to Dyslexico Premium. If you’re looking to use Dyslexico in your school district, tell your
                administrators about us! We’re always happy to talk to schools, and you can reach us at
                dyslexicosolutions@gmail.com{' '}
              </Typography>
            </>
          ) : (
            <Typography>
              You can use our text editor free for a limited number of times each day. For unlimited access, you can
              subscribe to Dyslexico Premium by going to{' '}
              <InlineTextButton href={`${websiteUrl}account`}>{websiteUrl}account</InlineTextButton>, signing in, and
              scrolling down to the subscribe button. If you’re looking to use Dyslexico in your school district, tell
              your administrators about us! We’re always happy to talk to schools, and you can reach us at
              dyslexicosolutions@gmail.com
            </Typography>
          ),
        },
        {
          question: 'How is this different from what I already get in Word, Google Docs, or Grammarly?',
          answer: (
            <Typography variant="body1" maxWidth={'900px'}>
              While those tools are helpful for the general population, they weren’t built with dyslexic people in mind.
              Dyslexico is meant to be a complement to those text editors with a higher rate of accuracy and tools
              catered to the dyslexic individual.
            </Typography>
          ),
        },
        {
          question: 'I’m typing a lot of data into Dyslexico - what happens to it?',
          answer: (
            <Typography variant="body1" maxWidth={'900px'}>
              Dyslexico is not only a spelling and grammar correction tool. It’s meant to identify which errors are
              consistently being made over a period of time and surface that information to its users and any educators
              they’re working with. As part of this, we store your sentences checked and errors corrected to categorize
              and identify areas of improvement. You can delete this information stored on our servers at any time by
              filling out the form found{' '}
              <InlineTextButton href="https://forms.gle/GFxgxWXKPV7RkWKw5">here.</InlineTextButton>
            </Typography>
          ),
        },
      ],
    },
    {
      title: 'User Questions',
      questions: [
        {
          question: 'How do I use Dyslexico?',
          answer: (
            <>
              <Typography>
                To use Dyslexico, you can look at the instructions through{' '}
                <InlineTextButton href={`${websiteUrl}tutorial`}>{websiteUrl}tutorial</InlineTextButton> or watch the
                following video:
              </Typography>
            </>
          ),
        },
        {
          question: 'Can I use Dyslexico on Google Docs or Chrome?',
          answer: (
            <Typography>
              To access Dyslexico’s spelling correction on other text editors, you can install Dyslexico’s Chrome
              extension onto your browser. To do so, follow this link by{' '}
              <InlineTextButton href="https://chrome.google.com/webstore/detail/dyslexico-chrome-extensio/pmpbmdhfeodffagambmfjcbfekofenjg">
                clicking here
              </InlineTextButton> {' '}
              and add to your device. Clicking the icon on your toolbar after it’s been installed causes the extension
              to appear on the side of your screen. Paste your writing into this and you’ll have access to the same
              powerful AI found on our website!
            </Typography>
          ),
        },
        {
          question: 'I was using Dyslexico, and it marked something wrong that was right - what do I do?',
          answer: (
            <Typography>
              If something went wrong or behaved in a way different from what you were expecting, tell us! Your feedback
              helps us improve Dyslexico greatly, and we love to see where we can improve.{' '}
              <InlineTextButton href="https://forms.gle/y4WAZcSGKMLQ3fEP7">Give us feedback here!</InlineTextButton>{' '}
            </Typography>
          ),
        },
        {
          question: 'I’m not a student. Can I still use Dyslexico?',
          answer: (
            <Typography>
              Dyslexico is intended to be used by those frequently typing online, whether it’s for school assignments or
              for everyday life. If you believe that existing tools aren’t catching enough of your errors, we encourage
              you to try using Dyslexico and see if there’s a difference!
            </Typography>
          ),
        },
      ],
    },
    {
      title: 'Educator and Parent Questions',
      questions: [
        {
          question: 'How can I see what my student is writing?',
          answer: (
            <>
              <Typography>
                You can see your student’s error analytics by adding them to your account. To do that, follow the
                instructions on the video found here:{' '}
                <InlineTextButton
                  href={`https://www.loom.com/share/15ab07475aa84bbaba5deb2b0896d822?sid=540f894d-7d0f-41f9-a0ef-9a38866a0501`}
                >
                  Add Educator to Student Account
                </InlineTextButton>
              </Typography>
            </>
          ),
        },
        {
          question: 'What is the intended age range for Dyslexico users?',
          answer: (
            <Typography>
              Dyslexico is intended to be used by those frequently typing online, whether it’s for school assignments or
              for everyday life. For students, Dyslexico is particularly intended to help students at an age after their
              classwork has shifted more to content than proper spelling.
            </Typography>
          ),
        },
        {
          question:
            'How can I use the analytics in Dyslexico to understand how my students are doing in their writing?',
          answer: (
            <Typography>
              Dyslexico analyzes and categorizes the errors your students make over a period of time. Given enough
              information, this can be used to identify strengths and areas of improvement for each individual student.
            </Typography>
          ),
        },
      ],
    },
    {
      title: 'Administrator Questions',
      questions: [
        {
          question: "How do I add the Chrome Extension to my students' computers?",
          answer: (
            <Typography>
              To access Dyslexico’s spelling correction on other text editors, you can install Dyslexico’s Chrome
              extension onto your browser. To do so, follow this link by{' '}
              <InlineTextButton href="https://chrome.google.com/webstore/detail/dyslexico-chrome-extensio/pmpbmdhfeodffagambmfjcbfekofenjg">
                clicking here
              </InlineTextButton>
              and add to your device. Clicking the icon on your toolbar after it’s been installed causes the extension
              to appear on the side of your screen. Paste your writing into this and you’ll have access to the same
              powerful AI found on our website!
            </Typography>
          ),
        },
        {
          question: 'If I have questions or concerns about the Terms of Service, who do I contact?',
          answer: (
            <Typography>
              Any questions or concerns can be sent to us at{' '}
              <InlineTextButton href="mailto:dyslexicosolutions@gmail.com">
                dyslexicosolutions@gmail.com.
              </InlineTextButton>
            </Typography>
          ),
        },
      ],
    },
  ];

  return (
    <Box id="faqId">
    <Typography variant="h2" align='center' >Frequently Asked Questions</Typography>
    {/* turn data above into tabs, then questions into accordions */}
    <Box sx={{ typography: 'body1', margin: 'auto', display: 'flex', flexGrow: 1, width: '100%'}}>
      <TabContext value={tabValue}>
        {/* Headers/Categories */}
        <TabList
          onChange={handleTabChange}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            whiteSpace: 'normal',
            minWidth: '100px',
            maxWidth: '20%',
          }} 
          variant="scrollable"
          orientation='vertical'
        >
          {/* Titles */}
          {faqData.map((category, index) => (
            <Tab label={category.title} value={index.toString()} wrapped />
          ))}
        </TabList>
        {/* Content */}
        {faqData.map((category, index) => (
          <TabPanel value={index.toString()} about={category.title} sx={{ maxWidth: '80%'}}>
            {category.questions.map((question, index) => (
              <Accordion sx={{ backgroundColor: grey[100] }}>
                <AccordionSummary
                  expandIcon={<FontAwesomeIcon icon={faArrowDown} data-testid="expandButton" size="lg" />}
                >
                  <Typography>{question.question}</Typography>
                </AccordionSummary>
                <AccordionDetails>{question.answer}</AccordionDetails>
              </Accordion>
            ))}
          </TabPanel>
        ))}
      </TabContext>
    </Box>
    </Box>
  );
}
export default FAQ;
